
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate, formatThousands, toMaximumTimeOfDay } from '@/utils'
import { createModel } from '@/utils/component'
import {
  batchJHDownloadTemplate, exportTransactionRecordHistory, getTransactionRecordHistoryPageList
} from '@/apis/paymentSlip/paymentTransactionRecord'
import dayjs from 'dayjs'
import {
  TransactionRecordHistoryModel,
  TransactionRecordHistoryPageParamModel
} from '@/model/paymentSlip/paymentTransactionRecordModel'
import { ElMessage } from 'element-plus'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import SearchFormForDate from '@/components/form/search/SearchFormForDate.vue'

export default defineComponent({
  name: 'JHTransactionRecordHistoryTab',
  components: {
    SearchFormForDate,
    BaseDialog,
    SimpleTable
  },
  props: {
    isActive: {
      type: Boolean,
      required: true
    },
    bankCode: {
      type: String,
      required: true
    },
    bankAccount: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n()
    const tableRef = ref()
    const paramModelRef = ref<TransactionRecordHistoryPageParamModel | null>(null)
    const disabledDate = (time: any) => {
      // 禁用昨天之后的日期
      return time.getTime() > Date.now() - 86400000 // 86400000 毫秒 = 1 天
    }
    const searchStruct = [
      {
        prop: 'trnType',
        label: t('pay.transactionType'),
        placeholder: t('pay.all'),
        component: 'el-select',
        type: 'text',
        model: null,
        options: [
          { label: t('pay.all'), value: null },
          { label: t('pay.transferOut'), value: '0' },
          { label: t('pay.transferInto'), value: '1' }
        ]
      },
      {
        prop: 'dateRange',
        label: t('pay.accountingDate'),
        component: 'el-date-picker',
        type: 'daterange',
        model: [],
        disabledDate: disabledDate
      }
    ]
    const JHTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      {
        prop: 'trnType',
        label: t('pay.transactionType'),
        formatter: (row: TransactionRecordHistoryModel) => row.trnType === '0' ? '转出' : '转入'
      },
      { prop: 'correlateName', label: t('pay.correlateName') },
      { prop: 'attachInfo', label: t('pay.attachInfo') },
      {
        prop: 'dtAcct',
        label: t('pay.accountingDate'),
        formatter: (row: TransactionRecordHistoryModel) => formatDate(row.dtAcct, 'yyyy-MM-dd')
      },
      {
        prop: 'trnAmt',
        label: t('pay.transactionAmount'),
        formatter: (row: TransactionRecordHistoryModel) => formatThousands(row.trnAmt),
        align: 'right'
      },
      {
        prop: 'currency',
        label: t('pay.currency'),
        formatter: (row: TransactionRecordHistoryModel) => row.currency === '01' ? 'CNY' : 'EUR'
      },
      {
        prop: 'balAmt',
        label: t('pay.balance'),
        formatter: (row: TransactionRecordHistoryModel) => formatThousands(row.balAmt),
        align: 'right'
      },
      { prop: 'businessType', label: t('pay.businessType') },
      { prop: 'purpose', label: t('pay.purpose') },
      { prop: 'summName', label: t('pay.abstractAbbreviation') },
      { prop: 'summDesc', label: t('pay.abstractDescription') },
      { prop: 'memo', label: t('pay.coreNote') }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<TransactionRecordHistoryModel>(),
      columnStruct: JHTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      datePickerDialogVisible: false,
      selectedDate: null as Date | null,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let dtAcctStart = null as string | null
        let dtAcctEnd = null as string | null
        if (dataMap.searchFormModel.dateRange && dataMap.searchFormModel.dateRange.length > 1) {
          dtAcctStart = dataMap.searchFormModel.dateRange[0] ? dayjs(dataMap.searchFormModel.dateRange[0])
            .format('YYYY-MM-DD HH:mm:ss') : null
          dtAcctEnd = dataMap.searchFormModel.dateRange[1] ? dayjs(toMaximumTimeOfDay(dataMap.searchFormModel.dateRange[1]))
            .format('YYYY-MM-DD HH:mm:ss') : null
        }
        const params: TransactionRecordHistoryPageParamModel = {
          bankCode: props.bankCode,
          trnType: dataMap.searchFormModel.trnType,
          dtAcctStart: dtAcctStart,
          dtAcctEnd: dtAcctEnd,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        try {
          const data = await getTransactionRecordHistoryPageList(params)
          if (data && data.data && data.data.list.length > 0) {
            dataMap.tableData = data.data.list
          } else {
            dataMap.tableData = []
          }
          dataMap.total = data?.data?.totalCount ?? 0
          paramModelRef.value = params
        } catch (error) {
          console.log(error)
          paramModelRef.value = null
        }

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      async handleExport() {
        if (!paramModelRef.value) {
          alert('请先进行检索')
          return
        }
        const params: TransactionRecordHistoryPageParamModel = {
          bankCode: paramModelRef.value.bankCode,
          trnType: paramModelRef.value.trnType,
          dtAcctStart: paramModelRef.value.dtAcctStart,
          dtAcctEnd: paramModelRef.value.dtAcctEnd,
          pageCurrent: paramModelRef.value.pageCurrent,
          pageSize: paramModelRef.value.pageSize
        }
        const urlBlob = await exportTransactionRecordHistory(params)
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = '建设银行交易流水.xlsx'
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取excel失败',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const handleDownloadHistoryRecord = () => {
      dataMap.selectedDate = null
      dataMap.datePickerDialogVisible = true
    }
    const onDatePickerDialogConfirm = async() => {
      if (dataMap.selectedDate != null) {
        const formData = new FormData() as any
        formData.append('vouchers', formatDate(dataMap.selectedDate))
        formData.append('bankCode', props.bankCode)
        const urlBlob = await batchJHDownloadTemplate(formData)
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = `CCB_${props.bankAccount}_${dataMap.selectedDate ? dayjs(dataMap.selectedDate)
            .format('YYYYMMDD') : ''}.zip`
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取文件失败',
            type: 'error',
            duration: 2000
          })
        }
        dataMap.datePickerDialogVisible = false
      } else {
        ElMessage({
          message: t('order.setPaymentDateP'),
          type: 'error'
        })
      }
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
    })
    return {
      t,
      tableRef,
      disabledDate,
      handleDownloadHistoryRecord,
      onDatePickerDialogConfirm,
      ...toRefs(dataMap)
    }
  }
})
